/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { Ad, AdBreak, AdContentTimePosition, AdPlaybackPolicyHandler, Promo } from '@quickplay/player-interface';
import { TIME_THRESHOLD } from './BrightcovePlayer';
import {
  createError,
  ErrorCodes,
  Error,
  HttpRequest,
  createRequestBuilder,
  createHttpClient,
  HttpResponse,
} from '@quickplay/foundation';

import { BrightcoveErrorCodes } from './BrightcoveErrorCodes';

/**
 * To fetch the Vmap document from given brightcove Ad Vmap Url
 * into an Ad Break.
 * @param adVMAPUrl
 */
export function fetchVmapDocument(adVMAPUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const request: HttpRequest<any> = createRequestBuilder()
      .url(adVMAPUrl)
      .get()
      .options({ disableEncode: true })
      .responseFormat('text')
      .build();
    const httpClient = createHttpClient<any, string>();
    httpClient
      .callWith(request)
      .then((response: HttpResponse<string>) => {
        if (response.body && response.code < 400) {
          resolve(response.body);
        } else {
          reject(createServerFailure(response.code));
        }
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

/**
 * Builds an Ad that represents each one of the provided AdCueMetadata and group them
 * into an Ad Break.
 * @param adCueMetadata list of Promos
 * @param duration the total duration of the content including Promos
 */
export function prepareAdBreak(adCueMetadata: Promo[] | undefined, duration: any): any {
  let adBreaks: AdBreak[] | undefined = undefined;
  let ads: Ad[] | undefined = undefined;

  if (adCueMetadata && adCueMetadata.length != 0) {
    adBreaks = [];
    ads = [];

    let adBreak: AdBreak | undefined;
    let nextAdBreakStart = 0;
    let adSequence = 0;

    adCueMetadata.forEach((promo: Promo, index: number) => {
      if (nextAdBreakStart === index) {
        adBreak = undefined;
        adSequence = 0;

        const { nextAdBreakStart_, adBreak_ } = evaluatePromosInRange(
          nextAdBreakStart,
          adCueMetadata,
          adBreak,
          promo,
          adBreaks,
          duration,
        );

        nextAdBreakStart = nextAdBreakStart_;
        adBreak = adBreak_;
      }

      ads?.push(toAd(promo, adSequence++, adBreak));
    });

    // return the ad breaks and the ads
    return {
      adBreaks_: adBreaks,
      ads_: ads,
    };
  }
}

/**
 * Builds the Ad Break corresponding to a list of promos.
 * @param nextAdBreakStart
 * @param promos the list of promos
 * @param adBreak the existing ad break
 * @param promo the promo to evaluate
 * @param adBreaks the list of ad breaks
 * @param duration the total duration of the content including Promos
 */
function evaluatePromosInRange(
  nextAdBreakStart: number,
  adCueMetadata: Promo[],
  adBreak: AdBreak | undefined,
  promo: Promo,
  adBreaks: AdBreak[] | undefined,
  duration: any,
): any {
  for (
    let promoIndexForAdBreak = nextAdBreakStart;
    promoIndexForAdBreak < adCueMetadata.length;
    promoIndexForAdBreak++
  ) {
    const promoForAdBreak: Promo = adCueMetadata[promoIndexForAdBreak];

    if (!adBreak) {
      adBreak = toAdBreak(promoForAdBreak, promo);
    } else if (truncateNumber(adBreak.startTime + adBreak.duration, 4) === promoForAdBreak.start) {
      adBreak.duration += promoForAdBreak.duration;
      adBreak.totalAds++;
    } else {
      nextAdBreakStart = promoIndexForAdBreak;

      if (adBreak) {
        adBreaks?.push(adBreak);
      }

      return {
        nextAdBreakStart_: nextAdBreakStart,
        adBreak_: adBreak,
      };
    }

    if (isPostroll(duration, adBreak)) {
      adBreak.position = AdContentTimePosition.POSTROLL;
    }

    if (promoIndexForAdBreak === adCueMetadata.length - 1) {
      if (adBreak) {
        adBreaks?.push(adBreak);
      }

      break;
    }
  }

  return {
    nextAdBreakStart_: nextAdBreakStart,
    adBreak_: adBreak,
  };
}

/**
 * Returns a boolean value indicating whether the provided ad break is postroll
 * or not.
 * @param duration the total duration of the content including Promos
 * @param adBreak the ad break used to determine whether it is postroll or not
 */
function isPostroll(duration: number, adBreak: AdBreak): boolean {
  return !isNaN(duration) && adBreak.startTime + adBreak.duration >= duration;
}

/**
 * Builds an Ad Break with the provided data.
 * @param promoForAdBreak the promo which contains the data for the new Ad Break
 * @param promo he promo which contains the data for the new Ad Break
 */
function toAdBreak(promoForAdBreak: Promo, promo: Promo): AdBreak {
  return {
    position:
      promoForAdBreak.contentTimePosition ||
      (promoForAdBreak.start === 0 ? AdContentTimePosition.PREROLL : AdContentTimePosition.MIDROLL),
    startTime: promoForAdBreak.start,
    duration: promo.duration,
    totalAds: 1,
    active: true,
    isSkippable: !!promo.skippable,
  };
}

/**
 * Builds an Ad with the provided data.
 * @param promo the promo which contains the data for the new Ad
 * @param adSequence the sequence number that corresponds to the new Ad
 * @param adBreak the Ad Break the new Ad belongs to
 */
function toAd(promo: Promo, adSequence: number, adBreak: AdBreak | undefined): Ad {
  return {
    adId: promo.contentId,
    sequence: adSequence + 1,
    start: promo.start,
    duration: promo.duration,
    title: '',
    isSkippable: promo.skippable ?? false,
    adBreak: adBreak,
    isAutoPlay: true,
    skipOffset: promo.skipOffset,
    contentDurationMs: promo.contentDurationMs,
  };
}

/**
 * Determines whether the provided playhead position is within an ad break or not.
 * @param playhead the playhead position in seconds
 * @param adBreaks the list of ad breaks
 */
export function isInAdBreak(playhead: number, adBreaks: AdBreak[] | undefined): AdBreak | null {
  if (!adBreaks) {
    return null;
  }

  for (const adBreak of adBreaks) {
    if (compare(adBreak.startTime, playhead) && playhead < adBreak.startTime + adBreak.duration) {
      return adBreak;
    }
  }

  return null;
}

/**
 * Determines whether the provided playhead position is within an ad or not.
 * @param playhead the playhead position in seconds
 * @param adBreaks the list of ad breaks
 * @param ads the list of ads
 */
export function isInAdvert(playhead: number, adBreaks: AdBreak[] | undefined, ads: Ad[] | undefined): Ad | null {
  if (!adBreaks || !ads) {
    return null;
  }

  for (const adBreak of adBreaks) {
    if (compare(adBreak.startTime, playhead) && playhead < adBreak.startTime + adBreak.duration) {
      const filteredAds: Ad[] = ads.filter((ad: Ad) => ad.adBreak?.startTime === adBreak.startTime);

      let adStart = adBreak.startTime;

      for (let i = 0; i < filteredAds.length; i++) {
        if (playhead < adStart + filteredAds[i].duration) {
          return filteredAds[i];
        }

        adStart += filteredAds[i].duration;
      }

      return null;
    }
  }

  return null;
}

/**
 * Returns the current Ad duration if any.
 * @param playhead the playhead position
 * @param adBreak the ad break
 * @param adBreaks the list of ad breaks
 * @param ads the list of ads
 */
export function currentAdDuration(
  playhead: number,
  adBreak: AdBreak,
  adBreaks: AdBreak[] | undefined,
  ads: Ad[] | undefined,
): number | null {
  if (!adBreaks) {
    return null;
  }

  const filteredAds: Ad[] = ads ? ads.filter((ad: Ad) => ad.adBreak?.startTime === adBreak.startTime) : [];

  let adStart = adBreak.startTime;

  for (let i = 0; i < filteredAds.length; i++) {
    if (compare(adStart, playhead) && playhead < adStart + filteredAds[i].duration) {
      return filteredAds[i].duration;
    }

    adStart += filteredAds[i].duration;
  }

  return null;
}

/**
 * Returns the total duration of the completed ad breaks.
 * @param playhead the playhead position
 * @param adBreak the list of ad breaks
 * @param ads the list of ads
 */
export function currentAdBreakCompletedAdsDuration(playhead: number, adBreak: AdBreak, ads: Ad[] | undefined): number {
  if (!ads) {
    return 0;
  }

  const filteredAds: Ad[] = ads.filter((ad: Ad) => ad.adBreak?.startTime === adBreak.startTime);

  let adStart = adBreak.startTime;

  let completedAdsDuration = 0;

  for (let i = 0; i < filteredAds.length; i++) {
    // if adStart less than or equal to playhead
    if (compare(adStart, playhead) && playhead < adStart + filteredAds[i].duration) {
      return completedAdsDuration;
    }

    adStart += filteredAds[i].duration;
    completedAdsDuration += filteredAds[i].duration;
  }

  return completedAdsDuration;
}

/**
 * Returns the ad end time corresponding to the current ad.
 * @param currentAd the current ad
 * @param ads the list of ads
 */
export function getCurrentAdEndTime(currentAd: Ad, ads: Ad[] | undefined): number {
  const completedCurrentAdBreakAdsDuration = ads
    ?.filter((ad: Ad) => ad.adBreak?.startTime === currentAd.adBreak?.startTime && ad.sequence < currentAd.sequence)
    .map((filteredAd: Ad) => filteredAd.duration)
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);

  if (completedCurrentAdBreakAdsDuration && currentAd.adBreak) {
    return currentAd.adBreak.startTime + completedCurrentAdBreakAdsDuration + currentAd.duration;
  }

  return currentAd.duration;
}

/**
 * Given two timestamps this function will return a boolean value indicating whether the first
 * timestamp is less than or equal to the second timestamp or not.
 *
 * Two timestamps are considered equal when the absolute value of the difference between the two
 * timestamps is less than TIME_THRESHOLD.
 *
 * @param first the first timestamp
 * @param second the second timestamp
 */
function compare(first: number, second: number): boolean {
  if (first < second) {
    return true;
  }

  return Math.abs(first - second) < TIME_THRESHOLD;
}

/**
 * Returns a boolean value indicating whethere there are promos available or not.
 * @param adBreaks the list of ad breaks
 */
export function isPromosAvailable(adBreaks: AdBreak[] | undefined): boolean {
  return !!(adBreaks && adBreaks.length);
}

/**
 * Determines whether there has been an Ad start event by comparing the player status
 * in two different playhead positions.
 * @param currentPlayhead the current playhead position
 * @param previousPlayhead the previous playhead position
 * @param adBreaks the list of ad breaks
 * @param ads the list of ads
 */
export function isAdStartEvent(
  currentPlayhead: number,
  previousPlayhead: number,
  adBreaks: AdBreak[] | undefined,
  ads: Ad[] | undefined,
): Ad | null {
  const currentAd = isInAdvert(currentPlayhead, adBreaks, ads);

  if (!currentAd) {
    return null;
  }

  const previousAd = isInAdvert(previousPlayhead, adBreaks, ads);

  if (previousAd) {
    if (currentAd.adId !== previousAd.adId) {
      //this.currentAd_ = currentAd;
      return currentAd;
    }
  } else {
    //this.currentAd_ = currentAd;
    return currentAd;
  }

  return null;
}

/**
 * Determines whether there has been an Ad Break start event by comparing the player
 * status in two different playhead positions.
 * @param currentPlayhead
 * @param previousPlayhead
 * @param adBreaks
 */
export function isAdBreakStartEvent(
  currentPlayhead: number,
  previousPlayhead: number,
  adBreaks: AdBreak[] | undefined,
): AdBreak | null {
  const adBreak = isInAdBreak(currentPlayhead, adBreaks);

  if (adBreak && !isInAdBreak(previousPlayhead, adBreaks)) {
    //this.currentAdBreak_ = adBreak;
    return adBreak;
  }

  return null;
}

/**
 * Determines whether there has been an Ad end event by comparing the player status
 * in two different playhead positions.
 * @param currentPlayhead
 * @param previousPlayhead
 * @param adBreaks
 * @param ads
 */
export function isAdEndEvent(
  currentPlayhead: number,
  previousPlayhead: number,
  adBreaks: AdBreak[] | undefined,
  ads: Ad[] | undefined,
): Ad | null {
  const previousAd = isInAdvert(previousPlayhead, adBreaks, ads);
  const currentAd = isInAdvert(currentPlayhead, adBreaks, ads);

  if (previousAd) {
    if (currentAd) {
      if (previousAd.adId !== currentAd.adId) {
        //this.currentAd_ = undefined;
        return previousAd;
      }
    } else {
      //this.currentAd_ = undefined;
      return previousAd;
    }
  }

  return null;
}

/**
 * Determines whether there has been an Ad Break end event by comparing the player
 * status in two different playhead positions.
 * @param currentPlayhead
 * @param previousPlayhead
 * @param adBreaks
 * @param currentTime
 * @param adPlaybackPolicyHandler
 */
export function isAdBreakEndEvent(
  currentPlayhead: number,
  previousPlayhead: number,
  adBreaks: AdBreak[] | undefined,
  currentTime: number,
  adPlaybackPolicyHandler: AdPlaybackPolicyHandler,
): AdBreak | null {
  const adBreak = isInAdBreak(previousPlayhead, adBreaks);

  if (!isInAdBreak(currentPlayhead, adBreaks) && adBreak) {
    //this.currentAdBreak_ = undefined;
    adPlaybackPolicyHandler.setWatchedAdBreak(adBreak);
    adPlaybackPolicyHandler.resolveSeekIntent(currentTime);

    return adBreak;
  }

  return null;
}

/**
 * Returns the total duration of all provided ad breaks.
 * @param adBreaks the list of ad breaks
 */
export function getTotalAdvertsBreaksDuration(adBreaks: AdBreak[]): number {
  return adBreaks
    .map((adBreak: AdBreak) => adBreak.duration)
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue);
}

/**
 * Returns the total duration of all provided ad breaks of which start time
 * is before the current time position.
 * @param currentTime the current time position.
 * @param adBreaks the list of ad breaks
 */
export function getAdvertBreaksDurationPriorTo(
  currentTime: number,
  adBreaks: AdBreak[],
  completedOnly = false,
): number {
  return adBreaks
    .map((adBreak: AdBreak) =>
      completedOnly
        ? adBreak.startTime < currentTime && adBreak.startTime + adBreak.duration < currentTime
          ? adBreak.duration
          : 0
        : adBreak.startTime < currentTime
          ? adBreak.duration
          : 0,
    )
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue);
}

/**
 * Returns the absolute position to seek for given a relative position. To calculate the absolute
 * position it calculates the total duration of all preroll ad breaks and all other breaks of which
 * start time is before the current time position.
 * @param playhead the content time position.
 * @param adBreaks the list of ad breaks
 */
export function getSeekPositionFor(playhead: number, adBreaks: AdBreak[], streamDuration = Number.NaN): number {
  // Prevent seek to negative playhead positions
  if (playhead < 0) {
    playhead = 0;
  }

  let streamPlayhead = streamTimeForContentTime(playhead, streamDuration, adBreaks ?? []);

  /**
   * NOTE: CBT-7553 [Web/Web TV] Brightcove SSAI Contents were not playing from start when the user seeks back.
   *
   * The seek position calculation logic has been updated to prevent double-counting ad break durations.
   * This issue was causing content to start at incorrect positions, especially when seeking backwards.
   * The function now correctly adjusts the playhead position without reapplying ad break durations
   * that have already been accounted for, ensuring content plays from the intended position.
   */

  // Identify the next ad break after the current playhead (before adjustment)
  const adBreakNextToSeekPosition = adBreaks.find((adBreak: AdBreak) => adBreak.startTime >= playhead);

  // Note:
  // Always check whether the computed main content playhead position falls in between adBreak or not. If the playhead position of the main content falls inside adBreak
  // then we need to compute new playhead by discarding the overlapping adBreak duration.
  if (
    adBreakNextToSeekPosition &&
    streamPlayhead >= adBreakNextToSeekPosition.startTime &&
    streamPlayhead < adBreakNextToSeekPosition.startTime + adBreakNextToSeekPosition.duration
  ) {
    streamPlayhead = adBreakNextToSeekPosition.startTime + adBreakNextToSeekPosition.duration;
  }

  return streamPlayhead;
}

/**
 * Returns a list of all active ad breaks of which start time is between the given range.
 * @param startPosition the start position.
 * @param endPosition the end position.
 * @param adBreaks the list of ad breaks
 */
export function getAdBreaksBetween(startPosition: number, endPosition: number, adBreaks: AdBreak[]): AdBreak[] {
  return adBreaks.filter(
    (adBreak: AdBreak) =>
      adBreak.active && adBreak.startTime >= startPosition && adBreak.startTime <= endPosition && adBreak.duration > 0,
  );
}

export function truncateNumber(number: number, decimals: number): number {
  return parseFloat(number.toFixed(decimals));
}

export function createServerFailure(statusCode: number): Error {
  return createError(ErrorCodes.ERROR_CATEGORY_MASK_SERVER, `vmap request failed with response code: ${statusCode}`);
}

export function createDocumentRetrievalFailure(internalError?: Error): Error {
  return createError(
    BrightcoveErrorCodes.VMAP_DOCUMENT_RETRIEVAL_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
    'Unable to return VMap response from the network request',
    internalError,
  );
}

export function createDocumentParsingFailure(): Error {
  return createError(
    BrightcoveErrorCodes.VMAP_DOCUMENT_PARSING_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
    'VMap document parsing failed',
  );
}

export function streamTimeForContentTime(contentTime: number, streamDuration: number, adBreaks: AdBreak[]): number {
  // Handle negative content time by returning preroll duration or 0
  if (contentTime < 0) {
    const preroll = adBreaks.find(adBreak => adBreak.startTime === 0);
    return preroll ? preroll.duration : 0;
  }

  // Include a zero-duration ad break at the start if no preroll exists
  const internalAdBreaks = adBreaks.some(adBreak => adBreak.startTime === 0)
    ? adBreaks
    : [{ startTime: 0, duration: 0 }, ...adBreaks];

  // Calculate the total duration of all ad breaks
  const totalAdBreaksDuration = internalAdBreaks.reduce((sum, adBreak) => sum + adBreak.duration, 0);

  // Check if the content time is beyond the last ad break, return the stream duration
  if (contentTime >= streamDuration - totalAdBreaksDuration) {
    return streamDuration;
  }

  // Iterate through the ad breaks to adjust the content time
  for (let i = 0; i < internalAdBreaks.length; i++) {
    const currentAdEnd = internalAdBreaks[i].startTime + internalAdBreaks[i].duration;
    const nextAdStart = internalAdBreaks[i + 1]?.startTime || streamDuration;

    // Time between current ad break end and next ad break start
    const gap = nextAdStart - currentAdEnd;

    // If remaining content time fits within the gap, return the corresponding stream time
    if (contentTime < gap) {
      return currentAdEnd + contentTime;
    }

    // Subtract the gap from the content time and continue
    contentTime -= gap;
  }

  // Return the original content time if all adjustments are processed
  return contentTime;
}

export function getCuePointsRelativeToContentTime(streamDuration: number, adBreaks: AdBreak[]): number[] {
  return [
    ...new Set(
      adBreaks.reduce((acc, adBreak, index) => {
        // Calc the total ad time by summing up the each ad duration.
        const totalAdTime = adBreaks.slice(0, index).reduce((sum, ad) => sum + ad.duration, 0);
        // Calc the relative content time subtracting the current ad start time by so far ad duration.
        const relativeContentTime = adBreak.startTime - totalAdTime;

        if (adBreak.position === AdContentTimePosition.POSTROLL) {
          // If the ad position is post roll, return actual content duratiom.
          acc.push(streamDuration);
        } else {
          acc.push(Math.max(relativeContentTime, 0));
        }
        return acc;
      }, [] as number[]),
    ),
  ];
}
